import React, { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';

/**
 * Manual crop modal used by CreateVideo / CreateVideoBeta when "Manual crop"
 * mode is enabled. The user pans/zooms to set a focus point. The result is
 * stored on each photo as a normalized object-position (`{ x, y }` percent,
 * 0-100) which the slideshow preview applies via CSS `object-position` and
 * which the backend forwards to the HP Server in the `crop_positions` array
 * for render-time application.
 *
 * We intentionally use focus-point cropping (not a fixed crop rect) because:
 *   1. Slideshow target aspect varies per orientation — the same source photo
 *      needs different crops in 9:16 vs 16:9. Storing a focus point lets the
 *      renderer choose the right window per orientation without re-cropping.
 *   2. CSS `object-fit: cover; object-position: x% y%;` exactly mirrors what
 *      ffmpeg's crop filter does at render time, so preview === output.
 */
export default function PhotoCropModal({
  open,
  imageUrl,
  initialPosition, // { x, y } 0-100 (percent), or undefined for default 50/50
  initialZoom,     // number, default 1
  aspect = 16 / 9, // target render aspect ratio
  onSave,          // ({ x, y, zoom }) => void
  onClose,
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(initialZoom || 1);
  const [focus, setFocus] = useState({
    x: initialPosition?.x ?? 50,
    y: initialPosition?.y ?? 50,
  });

  // Reset internal state every time the modal opens for a new photo.
  useEffect(() => {
    if (open) {
      setCrop({ x: 0, y: 0 });
      setZoom(initialZoom || 1);
      setFocus({
        x: initialPosition?.x ?? 50,
        y: initialPosition?.y ?? 50,
      });
    }
  }, [open, imageUrl, initialPosition, initialZoom]);

  // When react-easy-crop reports the crop area in pixels, translate it to a
  // focus point (the centre of the visible window expressed as % of image).
  const onCropComplete = useCallback((_area, areaPixels) => {
    // areaPixels is the visible window in source-pixel coords. Convert centre.
    const img = new Image();
    img.onload = () => {
      const cx = (areaPixels.x + areaPixels.width / 2) / img.naturalWidth * 100;
      const cy = (areaPixels.y + areaPixels.height / 2) / img.naturalHeight * 100;
      setFocus({
        x: Math.max(0, Math.min(100, cx)),
        y: Math.max(0, Math.min(100, cy)),
      });
    };
    img.src = imageUrl;
  }, [imageUrl]);

  // Esc key closes the modal (consistent app-wide pattern).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="photo-crop-modal-overlay"
    >
      <div className="relative w-full max-w-2xl">
        <button
          onClick={onClose}
          aria-label="Close crop"
          title="Close (Esc)"
          data-testid="photo-crop-modal-close-btn"
          className="absolute -top-3 -right-3 z-[90] p-2 rounded-full bg-black/70 hover:bg-black/90 text-white backdrop-blur-sm border border-white/10 transition-colors shadow-lg"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="bg-zinc-900 rounded-xl overflow-hidden shadow-2xl border border-white/10">
          {/* Crop canvas */}
          <div className="relative w-full bg-black" style={{ height: '60vh', maxHeight: 480 }}>
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              showGrid
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>

          {/* Controls */}
          <div className="p-4 space-y-3">
            <div>
              <Label className="text-zinc-300 text-xs">Zoom</Label>
              <input
                type="range"
                min={1}
                max={4}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="w-full accent-primary"
                data-testid="photo-crop-zoom-slider"
              />
            </div>

            <p className="text-[11px] text-white/50 text-center">
              Drag to pan · Scroll to zoom · Focus point: {focus.x.toFixed(0)}% × {focus.y.toFixed(0)}%
            </p>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1 bg-white/5 border-white/10 text-white hover:bg-white/10"
                data-testid="photo-crop-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={() => onSave({ x: focus.x, y: focus.y, zoom })}
                className="flex-1 btn-primary"
                data-testid="photo-crop-save-btn"
              >
                Save crop
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
